import { useQuery } from '@apollo/client'
import {
  MDXEditor,
  MDXEditorMethods,
  listsPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  CreateLink,
  linkPlugin,
  linkDialogPlugin,
  ListsToggle
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { Box, Button, FormControl, FormLabel, Input, Radio, RadioGroup, Select, Stack, Text, Textarea } from '@chakra-ui/react'
import React, { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { formElementInterface, keyValue, keyValueObject, keyValuePair, selectBoxOptionsInterface, systemDocumentInterface } from '../lib/types'
import { fileResult, keyValueObjectToSelectOptionsArray } from '../pages/systemForm.support'
import { GET_ALL_TYPES } from '../queries/queries'
import { CreateNotes } from './createNotes'
import { fileInterface, MultiFileUpload } from './MultiFileUpload/multiFileUpload.main'
import { formatDocTypeToFileUpload } from './MultiFileUpload/multiFileUpload.support'
import { SnapSelect } from './select'
import { SingleFileUpload, URLType } from './singleFileUpload'

export const FormComponent = ({
  inputData,
  onSubmitCallBack,
  cssProps,
  onChangeCallBack,
  renderForm
}: {
  inputData: formElementInterface[]
  onSubmitCallBack: (formData: keyValue, selectBoxLocalValues: keyValueObject, groupElementLocalValues: keyValueObject) => void
  cssProps?: keyValue
  onChangeCallBack?: (formData: keyValueObject) => void
  renderForm: number
}): JSX.Element => {
  const { data: allTypes } = useQuery(GET_ALL_TYPES) as {
    data: keyValue
  }
  const [filledFormValue, setFilledFormValue] = useState<keyValueObject>({})
  const [selectBoxLocalValues, setSelectBoxLocalValues] = useState<keyValueObject>({})
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [groupElementLocalValues, setGroupElementLocalValues] = useState<keyValueObject>({})
  const getTypedValue = (value: string, type: string): number | string => {
    switch (type) {
      case 'number':
        return parseInt(value, 10)
      case 'float':
        return parseFloat(value)

      default:
        return value
    }
  }
  const mdxRef = useRef<MDXEditorMethods>(null)
  const mapFormElements = (
    formElementData: formElementInterface,
    onChangeCallBack: ({
      event,
      elementName,
      isRadio = false,
      radioValue,
      directValue
    }: {
      event?: React.ChangeEvent
      elementName: string
      isRadio: boolean
      radioValue?: string
      directValue?: keyValueObject | keyValuePair[] | keyValueObject[] | string | fileResult[] | URLType
    }) => void
  ): JSX.Element => {
    const styleProps = {
      mx: '2',
      mb: '6',
      fontSize: '14px'
    }
    const labelProps = {
      fontSize: '14px',
      fontWeight: 'bold'
    }
    const {
      placeHolder,
      title,
      validation,
      chakraProps,
      selectOptions,
      type,
      name,
      value,
      elementOnChange,
      groupValuesToDisplay,
      groupingLogic,
      testId
    } = formElementData

    switch (type ?? '') {
      case 'select':
        return (
          <FormControl {...styleProps} {...(formElementData.holderProps ?? {})}>
            <FormLabel {...labelProps}>
              {title} {validation?.required && <sup color="red">*</sup>}
            </FormLabel>
            <Select
              variant={'outline'}
              {...(chakraProps ?? {})}
              {...(validation ?? {})}
              data-testid={testId}
              placeholder={placeHolder}
              name={name}
              fontSize="14px"
              onChange={e => {
                const HTMLElement = e.currentTarget as HTMLSelectElement
                if (elementOnChange) {
                  elementOnChange(HTMLElement.value, HTMLElement.getAttribute('name') as string)
                }
                const optionsList = Array.from(e.currentTarget.options)
                const selectedOption = optionsList.filter(
                  (optionElement: HTMLOptionElement) => optionElement.getAttribute('value') === HTMLElement.value
                )
                HTMLElement.setAttribute('data-selected-display-text', selectedOption[0].innerText)
                setSelectBoxLocalValues((prev: keyValueObject) => ({
                  ...prev,
                  [name]: { displayValue: selectedOption[0].innerText }
                }))
                onChangeCallBack({
                  event: e,
                  elementName: name,
                  isRadio: false
                })
              }}
              defaultValue={value as string}
            >
              {placeHolder !== null && <option value="">{placeHolder}</option>}
              {selectOptions?.map(
                (selectOption: selectBoxOptionsInterface): ReactNode => (
                  <option key={`${selectOption?.value}_key`} value={selectOption?.value}>
                    {selectOption?.display}
                  </option>
                )
              )}
            </Select>
          </FormControl>
        )
      case 'url':
        return (
          <SingleFileUpload
            title={title}
            required={validation?.required ?? false}
            placeHolder={placeHolder ?? ''}
            testId={testId}
            name={name}
            defaultValue={value as unknown as URLType}
            callBack={(callBackValue: URLType) => {
              onChangeCallBack({
                elementName: name,
                isRadio: false,
                directValue: callBackValue
              })
            }}
          />
        )
      case 'text':
      case 'search':
      case 'number':
      case 'date':
        return (
          <FormControl {...styleProps} {...(formElementData.holderProps ?? {})}>
            <FormLabel {...labelProps}>
              {title} {validation?.required && <sup color="red">*</sup>}
            </FormLabel>
            <Input
              placeholder={placeHolder}
              data-testid={testId}
              type={type}
              name={name}
              fontSize="14px"
              onChange={(e: ChangeEvent<Element>) => {
                const HTMLElement = e.currentTarget as HTMLFormElement

                if (elementOnChange) {
                  elementOnChange(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    getTypedValue(HTMLElement.value, type),
                    HTMLElement.getAttribute('name') as string
                  )
                }

                onChangeCallBack({
                  event: e,
                  elementName: name,
                  isRadio: false
                })
              }}
              defaultValue={value as string}
              variant={'outline'}
              {...(chakraProps ?? {})}
              {...(validation ?? {})}
            />
          </FormControl>
        )
      case 'textArea':
        return (
          <Box width="100%">
            <FormControl {...styleProps} {...(formElementData.holderProps ?? {})} width="100%" pr="3">
              <FormLabel {...labelProps}>
                {title} {validation?.required && <sup color="red">*</sup>}
              </FormLabel>
              <Textarea
                placeholder={placeHolder}
                data-testid={testId}
                name={name}
                fontSize="14px"
                onChange={(e: ChangeEvent<Element>) => {
                  const HTMLElement = e.currentTarget as HTMLFormElement

                  if (elementOnChange) {
                    elementOnChange(
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                      getTypedValue(HTMLElement.value, type),
                      HTMLElement.getAttribute('name') as string
                    )
                  }

                  onChangeCallBack({
                    event: e,
                    elementName: name,
                    isRadio: false
                  })
                }}
                defaultValue={value as string}
                variant={'outline'}
                {...(chakraProps ?? {})}
                {...(validation ?? {})}
              />
            </FormControl>
          </Box>
        )
      case 'groupElement': {
        const groupValues: string[] = (groupValuesToDisplay as string[]) ?? []
        const getGroupStringArray = (): string[] => {
          const group = groupValues.map(key => {
            const selectedSelectBox = selectBoxLocalValues[key] as keyValue
            if (key.includes('select') && typeof selectedSelectBox !== 'undefined') {
              return selectedSelectBox?.displayValue as string
            }
            return filledFormValue[key] as string
          })
          return group
        }

        const groupResult =
          typeof groupingLogic === 'function'
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              (groupingLogic(getGroupStringArray()) as string)
            : JSON.stringify(getGroupStringArray())

        return (
          <>
            <FormControl {...styleProps} {...(formElementData.holderProps ?? {})}>
              <FormLabel {...labelProps}>
                {title} {validation?.required && <sup color="red">*</sup>}
              </FormLabel>
              <Input
                placeholder={placeHolder}
                data-testid={testId}
                type={type}
                name={name}
                fontSize="14px"
                value={groupResult}
                onChange={() => {}}
                variant={'outline'}
                {...(chakraProps ?? {})}
                {...(validation ?? {})}
              />
            </FormControl>
          </>
        )
      }
      case 'notesElement': {
        return (
          <CreateNotes
            onChangeCallBack={e => {
              const HTMLElement = e.currentTarget as HTMLFormElement
              if (elementOnChange) {
                elementOnChange(HTMLElement.value as string, HTMLElement.getAttribute('name') as string)
              }
            }}
            styleProps={styleProps}
            labelProps={labelProps}
            chakraProps={chakraProps}
            formElementData={formElementData}
            inputData={value as keyValuePair[]}
            formattedDataCallBack={formattedData => {
              onChangeCallBack({
                elementName: name,
                isRadio: false,
                directValue: formattedData
              })
            }}
          />
        )
      }

      case 'radio': {
        return (
          <FormControl {...styleProps} {...(formElementData.holderProps ?? {})}>
            <FormLabel {...labelProps}>
              {title} {validation?.required && <sup color="red">*</sup>}
            </FormLabel>
            <RadioGroup
              {...(chakraProps ?? {})}
              {...(validation ?? {})}
              onChange={radioValue => {
                onChangeCallBack({
                  elementName: name,
                  isRadio: true,
                  radioValue
                })
              }}
              defaultValue={(value as string) ? value?.toString() ?? 'false' : 'false'}
            >
              <Stack direction="row" fontSize="14px">
                {selectOptions?.map(
                  (selectOption: selectBoxOptionsInterface, index: number): ReactNode => (
                    <Radio
                      name={`radio_${name}`}
                      data-testid={`${testId}_${index}`}
                      fontSize="14px"
                      key={`${selectOption?.value}_key`}
                      value={selectOption?.value}
                    >
                      <Text fontSize="14px">{selectOption?.display}</Text>
                    </Radio>
                  )
                )}
              </Stack>
            </RadioGroup>
          </FormControl>
        )
      }
      case 'submit':
        return (
          <Box {...(formElementData.holderProps ?? {})}>
            <FormControl {...styleProps} mt="4">
              <Button fontSize={'14px'} data-testid={testId} type="submit" colorScheme="blue" {...formElementData?.chakraProps}>
                {formElementData.title}
              </Button>
            </FormControl>
          </Box>
        )
      case 'multiSelect': {
        const typedValue = typeof (value as keyValue[]) !== 'object' ? [] : (value as keyValue[])

        const typeName = typedValue.length > 0 ? typedValue[0].__typename : ''

        const dataKeyNameObject = ((): {
          outerKey: string
          innerKey: string
        } => {
          switch (typeName) {
            case 'ProductInstruction':
              return {
                outerKey: 'product_instruction_list',
                innerKey: 'instruction_code'
              }
            default:
              return {
                outerKey: 'report_standard_list',
                innerKey: 'standard_name'
              }
          }
        })()
        interface multiselectInnerDataKeyInterface {
          id: string
          standard_name: string
          instruction_code: string
        }

        const defaultValue = typedValue?.reduce((reducer: keyValueObject[], item) => {
          const innerData = item[dataKeyNameObject.outerKey] as unknown as multiselectInnerDataKeyInterface
          return [
            ...reducer,
            {
              id: innerData.id,
              value: innerData.id,
              label: innerData.standard_name ?? innerData.instruction_code
            }
          ]
        }, [])

        return (
          <SnapSelect
            onChangeCallBack={(data, name) => {
              onChangeCallBack({
                elementName: name,
                isRadio: false,
                directValue: data
              })
            }}
            defaultValues={defaultValue}
            styleProps={styleProps}
            labelProps={labelProps}
            chakraProps={chakraProps}
            formElementData={formElementData}
            inputData={selectOptions}
            formattedDataCallBack={formattedData => {
              onChangeCallBack({
                elementName: name,
                isRadio: false,
                directValue: formattedData
              })
            }}
          />
        )
      }
      case 'multiFileUpload': {
        return (
          <MultiFileUpload
            options={keyValueObjectToSelectOptionsArray(allTypes.documentTypes as unknown as keyValue[], 'document_type')}
            defaultData={formatDocTypeToFileUpload(value as unknown as systemDocumentInterface[])}
            onChangeCallBack={data => {
              const processedResult = data.reduce(
                (reducer: fileResult[], item: fileInterface) => [
                  ...reducer,
                  {
                    id: item.product_doc_id ?? '',
                    document_type: { id: String(item.fileType.id) },
                    document_url: item.file_id ? String(item.file_id) : null,
                    is_private_file: Boolean(item.isPrivateFile)
                  }
                ],
                [] as fileResult[]
              )

              onChangeCallBack({
                elementName: name,
                isRadio: false,
                directValue: processedResult
              })
            }}
          />
        )
      }
      case 'richText': {
        return (
          <Box m="2" w="100%" borderBottom="1px solid" borderColor={'gray.100'} pb="4">
            <FormLabel {...labelProps}>{title}</FormLabel>
            <Box border="1px solid" borderColor={'gray.100'} borderRadius="8px" w="100%">
              <MDXEditor
                contentEditableClassName="snap-MDX-editor"
                markdown={value as string}
                ref={mdxRef}
                plugins={[
                  listsPlugin(),
                  linkPlugin(),
                  linkDialogPlugin(),
                  toolbarPlugin({
                    toolbarContents: () => (
                      <>
                        <UndoRedo /> <BoldItalicUnderlineToggles /> <CreateLink /> <ListsToggle />
                      </>
                    )
                  })
                ]}
              />
            </Box>
            <Button
              m="2"
              onClick={() => {
                onChangeCallBack({
                  elementName: name,
                  isRadio: false,
                  directValue: mdxRef.current?.getMarkdown()
                })
              }}
            >
              Save
            </Button>
          </Box>
        )
      }
      default:
        return <></>
    }
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions

    setFilledFormValue(
      inputData?.reduce((previousValue, currentValue) => {
        if (
          currentValue.type !== 'submit' &&
          currentValue.type !== 'button' &&
          currentValue.type !== 'multiSelect' &&
          currentValue.type !== 'notesElement'
        ) {
          return {
            ...previousValue,
            [currentValue.name]: currentValue.value ?? ''
          }
        }
        if (currentValue.type === 'multiSelect') {
          const getKey = ((): string => {
            switch (currentValue.originalKey) {
              case 'product_instructions':
                return 'product_instruction_list'

              default:
                return 'report_standard_list'
            }
          })()

          const typeCastedCurrentValue = currentValue.value as unknown as keyValue
          if (
            typeof typeCastedCurrentValue !== 'undefined' &&
            typeof typeCastedCurrentValue !== null &&
            (typeCastedCurrentValue as unknown as string) !== ''
          ) {
            // Raw array from DB: each item contains the nested [getKey] object
            if (Array.isArray(typeCastedCurrentValue)) {
              const reportStandardList = (typeCastedCurrentValue as unknown as keyValueObject[]).reduce(
                (reducer: keyValueObject[], item) => {
                  const inner = item[getKey] as keyValueObject
                  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                  if (inner?.id) {
                    return [...reducer, { [getKey]: { id: inner.id as string } }]
                  }
                  return reducer
                },
                []
              )
              return { ...previousValue, [currentValue.name]: reportStandardList }
            }

            const currentValueReportStandardList = typeCastedCurrentValue[getKey] as unknown as keyValueObject[]

            if (
              // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
              currentValueReportStandardList &&
              currentValueReportStandardList.length > 0
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const reportStandardList = currentValueReportStandardList?.reduce(
                (reducer: keyValueObject[], item) => [...reducer, { [getKey]: { id: item.id as string } }],
                []
              )
              return {
                ...previousValue,
                [currentValue.name]: reportStandardList ?? ''
              }
            }
          }
        }
        if (currentValue.type === 'notesElement') {
          const typeCastedCurrentValueNotes = currentValue.value as unknown as keyValue[]

          if (
            typeof typeCastedCurrentValueNotes !== 'undefined' &&
            typeof typeCastedCurrentValueNotes !== null &&
            (typeCastedCurrentValueNotes as unknown as string) !== ''
          ) {
            const tynameRemovedNotes = typeCastedCurrentValueNotes?.reduce(
              (reducer: keyValue[], item) => [...reducer, { notes: item.notes, id: item.id, is_active: item.is_active }],
              []
            )

            return {
              ...previousValue,
              [currentValue.name]: tynameRemovedNotes ?? ''
            }
          }
        }

        return { ...previousValue }
      }, {})
    )
    setSelectBoxLocalValues(
      inputData?.reduce((previousValue, currentValue) => {
        if (currentValue.type === 'select') {
          const selectedValue: selectBoxOptionsInterface[] | undefined = currentValue.selectOptions?.filter(
            optionElement => optionElement.value === currentValue.value
          )
          const [selectedResult] = selectedValue ?? []

          return {
            ...previousValue,
            [currentValue.name]: {
              id: currentValue.value,
              displayValue: selectedResult?.display ?? ''
            }
          }
        }
        return { ...previousValue }
      }, {})
    )
  }, [])

  const originalKeyMappings: keyValue = inputData?.reduce((reducer, item: formElementInterface): keyValue => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return { ...reducer, [item.name]: item.originalKey }
  }, {})

  const onFormChange = ({
    event,
    keyName,
    isRadio,
    radioValue,
    directValue
  }: {
    event?: React.ChangeEvent
    keyName: string
    isRadio: boolean
    radioValue: string | undefined
    directValue?: keyValueObject | keyValuePair[] | keyValueObject[] | string | fileResult[] | URLType
  }): void => {
    if (typeof directValue !== 'undefined') {
      setFilledFormValue((prev: keyValueObject) => ({
        ...prev,
        [keyName]: directValue
      }))
    }
    if (event) {
      const element = event.target as HTMLInputElement
      const value = element.value ?? ''
      const type = element.type

      if (typeof directValue === 'undefined') {
        setFilledFormValue(prev => ({
          ...prev,
          [keyName]: type === 'number' ? parseFloat(value) : value
        }))
      }
    }
    if (isRadio) {
      setFilledFormValue(prev => ({
        ...prev,
        [keyName]: radioValue as string
      }))
    }

    if (onChangeCallBack) {
      onChangeCallBack(filledFormValue)
    }
  }
  const mapWithOriginalKeys = (): keyValue => {
    const keys = Object.keys(filledFormValue)
    const reducedValue = keys.reduce((reducer, item) => {
      const originalMappingKey = originalKeyMappings[item] as string

      return {
        ...reducer,
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        [originalMappingKey]: filledFormValue[item] ? filledFormValue[item] : null
      }
    }, {})
    return reducedValue
  }
  const ref = useRef(null)

  return (
    <form
      ref={ref}
      name="form"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault()
        onSubmitCallBack(mapWithOriginalKeys(), selectBoxLocalValues, groupElementLocalValues)
      }}
      style={{ ...(cssProps ?? {}) }}
    >
      {renderForm &&
        inputData.map((formElementData, index) => (
          <React.Fragment key={`${formElementData.name}__${index}`}>
            {mapFormElements(formElementData, ({ event, elementName, isRadio, radioValue, directValue }) => {
              onFormChange({
                event,
                keyName: elementName,
                isRadio,
                radioValue,
                directValue
              })
            })}
          </React.Fragment>
        ))}
    </form>
  )
}
